import React, { useContext } from "react";
import { Controller, useForm } from "react-hook-form";
import { Button, Dialog } from "iuvity-ui-components";
import { Dropdown } from "iuvity-ui-components/dist/dropdown";
import { CustomInput } from "../inputs/CustomInput";
import { ApplicationCreatorSchema } from "../../validations/schemas";
import { DialogProps } from "../dialog/CustomDialog";
import { MultiContentDialogProps } from "../dialog/MultiContentDialog";
import { AppContext } from "../../context";
import { ErrorMessage } from "@hookform/error-message";

interface ApplicationCreatorProps {
  options: any;
  tenantid?: string;
  createFn: Function;
  reloadFn: Function;
  modalController?: React.Dispatch<React.SetStateAction<DialogProps>>;
  modalMcController?: React.Dispatch<
    React.SetStateAction<MultiContentDialogProps>
  >;
  auxModalController: React.Dispatch<React.SetStateAction<DialogProps>>;
}

export const applicationCreatorDefaultValues = {
  name: "",
  segment: "",
  tenantid: "",
  currentversion: "",
  installedversion: "",
};

export const ApplicationCreator: React.FC<ApplicationCreatorProps> = ({
  options,
  tenantid,
  createFn,
  reloadFn,
  modalController,
  modalMcController,
  auxModalController,
}) => {
  const {
    t,
    appState: { user },
  } = useContext(AppContext);

  const {
    control,
    getValues,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: tenantid
      ? {
          ...applicationCreatorDefaultValues,
          tenantid,
        }
      : applicationCreatorDefaultValues,
  });

  const onCancel = () => {
    if (modalMcController) {
      modalMcController((prev) => ({
        ...prev,
        visible: false,
        children: null,
      }));
    } else if (modalController) {
      modalController((prev) => ({
        ...prev,
        visible: false,
      }));
    }
    reset(applicationCreatorDefaultValues);
  };

  const onCreateApp = async () => {
    const formValues = getValues();
    onCancel();
    const actionStatus = await createFn("CREATE", [
      {
        ...formValues,
        createdby: user?.name,
      },
    ]);
    if (actionStatus === "SUCCESS") {
      reloadFn();
    }
  };

  const handleModalProps = () => {
    auxModalController((prev) => ({
      ...prev,
      visible: true,
      title: t(`Applications.createModalTitle`),
      content: t(`Applications.createModalContent`),
      onConfirm: onCreateApp,
    }));
  };

  const renderFooter = () => {
    return (
      <div className="flex align-items-center justify-content-flex-end">
        <Button
          label={t("Applications.cancelBtnLabel")}
          onClick={onCancel}
          className="mr-4"
          variant="p-button-link"
        />
        <Button
          label={t("Applications.createBtnLabel")}
          variant="p-button"
          onClick={handleModalProps}
          disabled={!isValid}
        />
      </div>
    );
  };

  return (
    <Dialog
      id="dialogScroll1"
      className="dialog-form"
      header={t("Applications.newApplicationTitle")}
      visible={true}
      onHide={onCancel}
      style={{ width: "25rem" }}
      footer={renderFooter}
    >
      <div className="form sm">
        <Controller
          key="name"
          name="name"
          control={control}
          rules={ApplicationCreatorSchema(t)?.name}
          render={({ field }) => {
            return (
              <CustomInput
                id="input-name"
                className={errors.name?.message ? "p-invalid" : "mb-3"}
                label={t(`Applications.nameLabel`)}
                placeholder={t(`Applications.namePlaceholder`)}
                type="text"
                autoComplete="off"
                keyfilter={ApplicationCreatorSchema(t)?.name?.keyfilter}
                maxLength={ApplicationCreatorSchema(t)?.name?.maxLength?.value}
                {...field}
              />
            );
          }}
        />
        <ErrorMessage
          errors={errors}
          name="name"
          //@ts-ignore
          render={({ message }) => (
            <small
              className="p-error block"
              style={{ marginTop: "0.5rem", marginBottom: "1rem" }}
            >
              {message}
            </small>
          )}
        />

       

        <Controller
          key="segment"
          name="segment"
          control={control}
          rules={ApplicationCreatorSchema(t).segment}
          render={({ field }) => {
            return (
              // @ts-ignore
              <Dropdown
                className="dropdown mb-3"
                label={t(`Applications.segmentLabel`)}
                placeholder={t(`Applications.segmentPlaceholder`)}
                optionLabel="label"
                optionValue="value"
                options={options.segments}
                filter={false}
                showClear={false}
                style={{
                  zIndex: "6000 !important",
                }}
                {...field}
              />
            );
          }}
        />

        <Controller
          key="currentversion"
          name="currentversion"
          rules={ApplicationCreatorSchema(t).currentversion}
          control={control}
          render={({ field }) => {
            return (
              // @ts-ignore
              <Dropdown
                className="dropdown mb-3"
                label={t(`Applications.currentversionLabel`)}
                placeholder={t(`Applications.currentversionPlaceholder`)}
                optionLabel="label"
                optionValue="value"
                options={options.versions}
                filter={false}
                showClear={false}
                style={{
                  zIndex: "6000 !important",
                }}
                {...field}
              />
            );
          }}
        />

      </div>
    </Dialog>
  );
};
